/**
 * @module useOrderDetailsAnalytics
 * @description Custom hook for Order Details page analytics tracking
 */

import { useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useCustomTealiumTrigger } from './useTealiumTrigger';
import { analytics } from '../tools/analytics';
import { getPageFindingMethod } from '../tools/analytics/pageFindingMethod';
import { collectDatabridgeDatapoints } from '../tools/tealium';
import { orderDetailsAnalyticsQueryStr } from '../query/fragmentStrings';

const ORDER_DETAILS_ANALYTICS_QUERY = gql`${orderDetailsAnalyticsQueryStr}`;

/**
 * @function useOrderDetailsAnalytics
 * @description Hook that handles Order Details page analytics tracking
 * @returns {Object} Analytics functions and state
 *
 */
export const useOrderDetailsAnalytics = () => {
  const { data: analyticsData, loading } = useQuery(ORDER_DETAILS_ANALYTICS_QUERY, {
    ssr: false,
    context: { batch: true },
    skip: typeof window === 'undefined' || !window?.digitalData, // Skip if digitalData is not available
  });

  // Set up Tealium trigger for my_account event
  const tealiumData = {
    eventName: 'my_account',
    eventData: {
      tealium_event: 'my_account',
      event_name: 'my_account',
    },
  };

  const triggerMyAccountEvent = useCustomTealiumTrigger(tealiumData);

  /**
   * @function setLoyaltyAnalyticsData
   * @description Sets loyalty-related analytics data
   * @param {Object} config - Configuration object with loyalty data
   * @param {Object} loyaltyInfo - Loyalty information object
   */
  const setLoyaltyAnalyticsData = useCallback((config, loyaltyInfo) => {
    if (!config || !analytics?.loyalty || typeof window === 'undefined') return;

    const {
      tierImageSrc,
      loyaltyPoints,
      loyaltyToNextReward,
    } = config;

    // Set VIP status based on tier image
    if (tierImageSrc) {
      analytics.loyalty.setTier(tierImageSrc);
    }

    // Set loyalty points
    if (loyaltyPoints !== undefined && loyaltyPoints !== null) {
      analytics.loyalty.setPoints(loyaltyPoints);
    }

    // Set points to next reward (calculate points needed)
    if (loyaltyToNextReward !== undefined && loyaltyToNextReward !== null && loyaltyPoints !== undefined) {
      const pointsToNext = Math.max(0, loyaltyToNextReward - loyaltyPoints);
      analytics.loyalty.setPointsToNextReward(pointsToNext);
    }

    // Set loyalty join date
    if (loyaltyInfo?.enrolledAt) {
      analytics.loyalty.setJoinDate(loyaltyInfo.enrolledAt);
    }
  }, []);

  /**
   * @function triggerOrderDetailsView
   * @description Triggers the Order Details page view analytics event
   */
  const triggerOrderDetailsView = useCallback(() => {
    if (loading || typeof window === 'undefined' || !window?.digitalData) return;

    const {
      userInfo,
      loyaltyInfo,
      userState,
      config,
    } = analyticsData || {};

    // Set user analytics data
    if (userInfo && analytics?.user) {
      analytics.user.setData({
        id: userInfo.id,
        hashedEmail: userInfo.hashedEmail,
      });
    }

    // Set loyalty analytics data
    if (userState?.isLoggedIn && config) {
      setLoyaltyAnalyticsData(config, loyaltyInfo);
    }

    const pageFindingMethod = getPageFindingMethod();

    const globalDatapoints = collectDatabridgeDatapoints(window.digitalData);

    // Create the analytics event data with all required fields
    const eventData = {
      tealium_event: 'my_account',
      event_name: 'my_account',
      page_finding_method: pageFindingMethod,
      ...globalDatapoints,
    };

    // Add loyalty data if user is logged in
    if (userState?.isLoggedIn && config) {
      if (loyaltyInfo?.enrolledAt) {
        // Format join date to M/D/YYYY format as shown in requirements
        const date = new Date(loyaltyInfo.enrolledAt);
        if (!Number.isNaN(date.getTime())) {
          const month = date.getUTCMonth() + 1;
          const day = date.getUTCDate();
          const year = date.getUTCFullYear();
          eventData.loyalty_join_date = `${month}/${day}/${year}`;
        }
      }

      if (config.loyaltyPoints !== undefined) {
        eventData.loyalty_points = config.loyaltyPoints;
      }

      if (config.loyaltyToNextReward !== undefined && config.loyaltyPoints !== undefined) {
        const pointsToNext = Math.max(0, config.loyaltyToNextReward - config.loyaltyPoints);
        eventData.loyalty_to_next_reward = pointsToNext;
      }

      if (config.tierImageSrc) {
        eventData.loyalty_vip_status = config.tierImageSrc.includes('vip') ? 'VIP' : 'base';
      }
    }

    // Trigger the Tealium event
    window.digitalData.trigger('my_account', eventData);

    // Also trigger using the custom hook for consistency
    triggerMyAccountEvent();
  }, [
    loading,
    analyticsData,
    setLoyaltyAnalyticsData,
    triggerMyAccountEvent,
  ]);

  return {
    triggerOrderDetailsView,
    loading,
    isReady: !loading && typeof window !== 'undefined' && !!window?.digitalData,
  };
};

export default useOrderDetailsAnalytics;
